/**
 * WorkspaceHomePage — standalone full-page project selector.
 *
 * Rendered OUTSIDE AppShell (no sidebar), sitting between /login and the main app.
 * Only active when featureFlags.workspaceHomePage = true (SPARC deployment).
 *
 * Behaviour:
 *  - If defaultTenantId is provided: auto-selects that tenant, shows only its projects.
 *  - If no defaultTenantId: shows a tenant list on the left, projects on the right.
 *  - Clicking a project card updates the workspace store and calls onEnter(route).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listTenants } from '../../api/tenants';
import { listProjects } from '../../api/projects';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useConfig } from '../../contexts/ConfigContext';
import { useAuthContext } from '../Auth/AuthProvider';
import type { Tenant, Project } from '../../types/api';
import {
  Building2,
  ChevronRight,
  LogOut,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { shouldAutoEnterSingleProject } from '../../utils/workspaceAutoSelect';

interface WorkspaceHomePageProps {
  /** Pre-configured tenant ID from config — when set, tenant picker is hidden */
  defaultTenantId?: string;
  /** Pre-configured project ID — auto-enter when set or when only one project exists */
  defaultProjectId?: string;
  /** Called with the route to navigate to after project is selected */
  onEnter: (path: string) => void;
}

const DEFAULT_ENTER_ROUTE = '/rfp/external-data';

export function WorkspaceHomePage({ defaultTenantId, defaultProjectId, onEnter }: WorkspaceHomePageProps) {
  const config = useConfig();
  const { user, logout } = useAuthContext();
  const { selectTenant, setProject, setProjects, projectId } = useWorkspace();

  const primaryColor = config?.branding?.primaryColor || '#0ea5e9';
  const platformTitle = config?.branding?.platformTitle || config?.branding?.appName || 'TraceOS';
  const platformSubtitle = config?.branding?.platformSubtitle || '';

  // ── Tenants ────────────────────────────────────────────────────────────────
  const { data: tenantsRaw, isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: listTenants,
    staleTime: 5 * 60 * 1000, // 5 min - tenants rarely change
    refetchOnWindowFocus: false,
  });
  const allTenants = useMemo<Tenant[]>(
    () => (Array.isArray(tenantsRaw) ? tenantsRaw : []),
    [tenantsRaw]
  );

  // If a defaultTenantId is configured, only allow switching within that tenant
  const fixedTenant = useMemo(
    () => (defaultTenantId ? allTenants.find((t) => t.id === defaultTenantId) ?? null : null),
    [allTenants, defaultTenantId]
  );
  const showTenantPicker = !defaultTenantId;

  const [activeTenantId, setActiveTenantId] = useState<string | null>(defaultTenantId ?? null);

  // Resolve the active tenant object
  const activeTenant = useMemo(
    () => allTenants.find((t) => t.id === activeTenantId) ?? null,
    [allTenants, activeTenantId]
  );

  // Auto-select first tenant when no default configured (single-tenant users)
  useEffect(() => {
    if (activeTenantId || allTenants.length === 0) return;
    if (allTenants.length === 1) {
      setActiveTenantId(allTenants[0].id);
      return;
    }
    setActiveTenantId(allTenants[0].id);
  }, [allTenants, activeTenantId]);

  // Sync tenant into workspace store when active tenant changes
  useEffect(() => {
    if (activeTenant) selectTenant(activeTenant);
  }, [activeTenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Projects ───────────────────────────────────────────────────────────────
  const { data: projectsRaw, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', activeTenantId],
    queryFn: () => listProjects(activeTenantId || undefined),
    enabled: !!activeTenantId,
    staleTime: 5 * 60 * 1000, // 5 min - projects rarely change
    refetchOnWindowFocus: false,
  });
  const projects = useMemo<Project[]>(
    () => (Array.isArray(projectsRaw) ? projectsRaw : []),
    [projectsRaw]
  );

  useEffect(() => {
    if (projects.length) setProjects(projects);
  }, [projects, setProjects]);

  const [entering, setEntering] = useState(false);

  const handleEnter = (project: Project) => {
    if (entering) return;
    setEntering(true);
    if (activeTenant) selectTenant(activeTenant);
    setProject(project);
    onEnter(DEFAULT_ENTER_ROUTE);
  };

  // Auto-enter when config default project or sole project is available
  useEffect(() => {
    if (entering || !projects.length || !activeTenant) return;

    const configured = defaultProjectId
      ? projects.find((p) => p.id === defaultProjectId)
      : undefined;
    const sole = shouldAutoEnterSingleProject(projects.length) ? projects[0] : undefined;
    const target = configured ?? sole;
    if (target) {
      handleEnter(target);
    }
  }, [projects, defaultProjectId, activeTenant?.id, entering]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Select a project and enter the app ────────────────────────────────────

  // ── Helpers ────────────────────────────────────────────────────────────────
  const initials = (name: string) => name.trim()[0]?.toUpperCase() ?? '?';

  const isLoading = tenantsLoading || projectsLoading;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0f172a' }}>
      {/* ── Top nav ── */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: primaryColor }}
          >
            {initials(platformTitle)}
          </div>
          <div>
            <span className="text-white font-semibold text-sm">{platformTitle}</span>
            {platformSubtitle && (
              <span className="ml-2 text-white/40 text-xs">{platformSubtitle}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {user?.email && (
            <span className="text-white/50 text-sm hidden sm:block">{user.email}</span>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-white/50 hover:text-white/80 text-sm transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Tenant sidebar (only shown when no defaultTenantId) ── */}
        {showTenantPicker && (
          <aside className="w-56 flex-shrink-0 border-r border-white/10 overflow-y-auto py-4">
            <p className="px-5 text-xs font-semibold uppercase tracking-widest text-white/30 mb-3">
              Organisations
            </p>
            {tenantsLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-white/30" />
              </div>
            )}
            {allTenants.map((t: Tenant) => {
              const active = t.id === activeTenantId;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTenantId(t.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    active ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                  }`}
                >
                  <div
                    className="h-7 w-7 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: active ? primaryColor : '#334155' }}
                  >
                    {initials(t.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{t.name}</p>
                  </div>
                  {active && <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />}
                </button>
              );
            })}
          </aside>
        )}

        {/* ── Project selector ── */}
        <main className="flex-1 overflow-y-auto px-8 py-10">
          {/* Org info when single-tenant */}
          {!showTenantPicker && activeTenant && (
            <div className="flex items-center gap-3 mb-8">
              <div
                className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {initials(activeTenant.name)}
              </div>
              <div>
                <h2 className="text-white text-xl font-bold">{activeTenant.name}</h2>
                {activeTenant.slug && (
                  <p className="text-white/40 text-sm">{activeTenant.slug}</p>
                )}
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-white text-lg font-semibold">Select a project</h3>
            <p className="text-white/40 text-sm mt-1">
              Choose a project to continue into the workspace
            </p>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center gap-3 text-white/40 py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading projects…</span>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && projects.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="h-10 w-10 mx-auto mb-3 text-white/20" />
              <p className="text-white/40 text-sm">No projects found.</p>
            </div>
          )}

          {/* Project cards */}
          {!isLoading && projects.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {projects.map((p: Project) => {
                const isActive = p.id === projectId;
                return (
                  <button
                    key={p.id}
                    onClick={() => handleEnter(p)}
                    disabled={entering}
                    className={`group relative text-left rounded-xl p-5 border transition-all hover:scale-[1.02] focus:outline-none disabled:opacity-60 ${
                      isActive
                        ? 'bg-white/10 border-white/30 shadow-lg shadow-black/30'
                        : 'bg-white/5 border-white/10 hover:bg-white/8 hover:border-white/20'
                    }`}
                    style={
                      isActive
                        ? { borderColor: `${primaryColor}80`, boxShadow: `0 0 0 1px ${primaryColor}40` }
                        : undefined
                    }
                  >
                    {/* Icon */}
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center mb-4"
                      style={{
                        backgroundColor: isActive ? `${primaryColor}30` : 'rgba(255,255,255,0.08)',
                      }}
                    >
                      <Building2
                        className="h-5 w-5"
                        style={{ color: isActive ? primaryColor : 'rgba(255,255,255,0.5)' }}
                      />
                    </div>

                    {/* Name */}
                    <p className="text-white font-semibold text-sm leading-snug mb-1">{p.name}</p>

                    {/* Description */}
                    {p.description && (
                      <p className="text-white/40 text-xs line-clamp-2">{p.description}</p>
                    )}

                    {/* Footer */}
                    <div className="mt-4 flex items-center justify-between">
                      {isActive ? (
                        <span
                          className="text-xs font-medium"
                          style={{ color: primaryColor }}
                        >
                          Currently active
                        </span>
                      ) : (
                        <span className="text-white/30 text-xs">
                          {p.created_at ? new Date(p.created_at).getFullYear() : ''}
                        </span>
                      )}
                      <ArrowRight
                        className="h-4 w-4 text-white/20 group-hover:text-white/60 transition-colors"
                        style={isActive ? { color: `${primaryColor}80` } : undefined}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
